(() => {
  const FUNDLET_PAGE_PREFIX = '/pages/fundlet/';
  const FUNDLET_CARD_SELECTOR = '.fundlet-collection-item.fundlet-product-card';
  const FUNDLET_PRICE_SELECTOR = '.fundlet-collection-price';
  const FUNDLET_CHECKOUT_LINK_SELECTOR =
    '.fundlet-product-dialog-success a[href="/checkout"], .fundlet-product-dialog-success a[href$="/checkout"]';
  const FUNDLET_DIALOG_ID = 'naturkidz-fundlet-checkout-dialog';
  const productCache = new Map();

  let activeElementBeforeDialog = null;
  let dialogElements;
  let enhancementQueued = false;

  function isFundletContext() {
    return (
      window.location.pathname.indexOf(FUNDLET_PAGE_PREFIX) === 0 ||
      document.querySelector(FUNDLET_CARD_SELECTOR) ||
      document.querySelector('.fundlet-product-dialog-buttons')
    );
  }

  function isCartAddUrl(urlString) {
    if (!urlString || typeof urlString !== 'string') {
      return false;
    }

    try {
      const path = new URL(urlString, window.location.href).pathname;
      return /\/cart\/add(\.js)?$/.test(path);
    } catch {
      return false;
    }
  }

  function shouldSyncCartAfterAddPayload(data) {
    if (!data || data.status) {
      return false;
    }

    if (data.sections) {
      return false;
    }

    if (window.theme?.settings?.cartType !== 'modal') {
      return false;
    }

    return Boolean(
      data.key ||
        data.variant_id ||
        (Array.isArray(data.items) && data.items.length > 0)
    );
  }

  function handlePossibleFundletCartAddResponseBody(bodyText) {
    if (!bodyText) {
      return;
    }

    let data;

    try {
      data = JSON.parse(bodyText);
    } catch {
      return;
    }

    if (!shouldSyncCartAfterAddPayload(data)) {
      return;
    }

    refreshShapesCartAfterFundletAdd();
  }

  function refreshShapesCartAfterFundletAdd() {
    document.dispatchEvent(new CustomEvent('theme:update:cart'));
  }

  function installFundletCartDrawerSync() {
    if (window.__nkFundletCartDrawerSync) {
      return;
    }

    window.__nkFundletCartDrawerSync = true;

    const nativeFetch = window.fetch.bind(window);

    window.fetch = function patchedFetch(input, init) {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof Request
            ? input.url
            : '';

      const chain = nativeFetch(input, init);

      if (!isCartAddUrl(url)) {
        return chain;
      }

      return chain.then((response) => {
        if (!response.ok) {
          return response;
        }

        const cloned = response.clone();

        cloned
          .text()
          .then(handlePossibleFundletCartAddResponseBody)
          .catch(() => {});

        return response;
      });
    };

    const xhrOpen = XMLHttpRequest.prototype.open;
    const xhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__nkRequestUrl = url;
      return xhrOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (body) {
      const requestUrl = this.__nkRequestUrl;

      if (isCartAddUrl(String(requestUrl || ''))) {
        this.addEventListener('load', function () {
          if (this.responseType && this.responseType !== '' && this.responseType !== 'text') {
            return;
          }

          handlePossibleFundletCartAddResponseBody(this.responseText);
        });
      }

      return xhrSend.call(this, body);
    };
  }

  if (!isFundletContext()) {
    return;
  }

  installFundletCartDrawerSync();

  injectStyles();
  enhanceFundletUi();

  document.addEventListener('click', handleDocumentClick);
  window.addEventListener('pageshow', queueEnhancements);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== 'childList') {
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) {
          continue;
        }

        if (
          node.matches?.(FUNDLET_CARD_SELECTOR) ||
          node.matches?.('.fundlet-product-dialog-buttons') ||
          node.querySelector?.(FUNDLET_CARD_SELECTOR) ||
          node.querySelector?.('.fundlet-product-dialog-buttons')
        ) {
          queueEnhancements();
          return;
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  function queueEnhancements() {
    if (enhancementQueued) {
      return;
    }

    enhancementQueued = true;
    window.requestAnimationFrame(() => {
      enhancementQueued = false;
      enhanceFundletUi();
    });
  }

  function handleDocumentClick(event) {
    const checkoutLink = event.target.closest(FUNDLET_CHECKOUT_LINK_SELECTOR);

    if (checkoutLink && isFundletContext()) {
      event.preventDefault();
      closeFundletProductDialog(checkoutLink);
      window.requestAnimationFrame(() => openDialog());
      return;
    }

    if (!dialogElements) {
      return;
    }

    if (event.target === dialogElements.backdrop || event.target.closest('[data-fundlet-dialog-close]')) {
      closeDialog();
    }
  }

  function injectStyles() {
    if (document.getElementById('naturkidz-fundlet-enhancements-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'naturkidz-fundlet-enhancements-styles';
    style.textContent = `
      .nk-fundlet-price-row {
        align-items: baseline;
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        justify-content: center;
        text-align: center;
        width: 100%;
      }

      .nk-fundlet-price-row > * {
        margin: 0;
      }

      .nk-fundlet-compare-price {
        color: inherit;
        margin: 0;
        opacity: 0.7;
        text-decoration: line-through;
      }

      .nk-fundlet-dialog-backdrop {
        align-items: center;
        background: rgba(17, 17, 17, 0.55);
        display: flex;
        inset: 0;
        justify-content: center;
        padding: 1.5rem;
        position: fixed;
        z-index: 2147483647;
      }

      .nk-fundlet-dialog-backdrop[hidden] {
        display: none;
      }

      .nk-fundlet-dialog {
        background: #fdf8de;
        border: 2px solid #5b8c5a;
        border-radius: 1rem;
        box-shadow: 0 20px 50px rgba(17, 17, 17, 0.18);
        color: #1d1d1d;
        max-width: 32rem;
        padding: 1.5rem;
        position: relative;
        width: min(100%, 32rem);
      }

      .nk-fundlet-dialog-title {
        font-size: 1.35rem;
        font-weight: 700;
        line-height: 1.2;
        margin: 0 0 0.75rem;
      }

      .nk-fundlet-dialog-message {
        line-height: 1.5;
        margin: 0;
      }

      .nk-fundlet-dialog-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        margin-top: 1.25rem;
      }

      .nk-fundlet-dialog-cart-link,
      .nk-fundlet-dialog-close {
        border-radius: 999px;
        cursor: pointer;
        font: inherit;
        padding: 0.85rem 1.2rem;
        text-align: center;
        text-decoration: none;
      }

      .nk-fundlet-dialog-cart-link {
        background: #5b8c5a;
        color: #fdf8de;
        font-weight: 700;
      }

      .nk-fundlet-dialog-close {
        background: transparent;
        border: 1px solid rgba(29, 29, 29, 0.2);
        color: inherit;
      }

      .nk-fundlet-dialog-icon-close {
        background: transparent;
        border: 0;
        color: inherit;
        cursor: pointer;
        font-size: 1.5rem;
        line-height: 1;
        padding: 0.25rem;
        position: absolute;
        right: 0.75rem;
        top: 0.75rem;
      }
    `;

    document.head.appendChild(style);
  }

  function ensureDialog() {
    if (dialogElements) {
      return dialogElements;
    }

    const backdrop = document.createElement('div');
    backdrop.className = 'nk-fundlet-dialog-backdrop';
    backdrop.hidden = true;
    backdrop.id = FUNDLET_DIALOG_ID;
    backdrop.innerHTML = `
      <div
        class="nk-fundlet-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="naturkidz-fundlet-dialog-title"
        aria-describedby="naturkidz-fundlet-dialog-message"
      >
        <button
          class="nk-fundlet-dialog-icon-close"
          type="button"
          aria-label="Close message"
          data-fundlet-dialog-close
        >
          &times;
        </button>
        <h2 class="nk-fundlet-dialog-title" id="naturkidz-fundlet-dialog-title">
          Fundraiser delivery reminder
        </h2>
        <p class="nk-fundlet-dialog-message" id="naturkidz-fundlet-dialog-message">
          All fundraiser products will be delivered to the school for pick up at the end of the fundraiser.
        </p>
        <div class="nk-fundlet-dialog-actions">
          <a class="nk-fundlet-dialog-cart-link" href="/cart">Proceed to cart</a>
          <button class="nk-fundlet-dialog-close" type="button" data-fundlet-dialog-close>
            Keep shopping
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(backdrop);

    dialogElements = {
      backdrop,
      cartLink: backdrop.querySelector('.nk-fundlet-dialog-cart-link'),
    };

    document.addEventListener('keydown', handleEscapeKey);

    return dialogElements;
  }

  function openDialog() {
    const { backdrop, cartLink } = ensureDialog();

    activeElementBeforeDialog = document.activeElement;
    backdrop.hidden = false;
    document.documentElement.classList.add('overflow-hidden');
    cartLink.focus();
  }

  function closeFundletProductDialog(checkoutLink) {
    const dialog =
      checkoutLink.closest('.fundlet-product-dialog') ||
      document.querySelector('.fundlet-product-dialog');

    const closeButton = dialog?.querySelector('.fundlet-product-dialog-close');

    if (closeButton instanceof HTMLElement) {
      closeButton.click();
    }
  }

  function closeDialog() {
    if (!dialogElements) {
      return;
    }

    dialogElements.backdrop.hidden = true;
    document.documentElement.classList.remove('overflow-hidden');

    if (activeElementBeforeDialog instanceof HTMLElement) {
      activeElementBeforeDialog.focus();
    }
  }

  function handleEscapeKey(event) {
    if (event.key === 'Escape' && dialogElements && !dialogElements.backdrop.hidden) {
      closeDialog();
    }
  }

  function enhanceFundletUi() {
    updateCheckoutLinkText();
    hideSaleBadges();
    enhanceGridPricing();
  }

  function updateCheckoutLinkText() {
    document.querySelectorAll(FUNDLET_CHECKOUT_LINK_SELECTOR).forEach((link) => {
      link.textContent = 'cart';
    });
  }

  function hideSaleBadges() {
    document.querySelectorAll('.fundlet-collection-badge').forEach((badge) => {
      if (badge.dataset.naturkidzProcessed === 'true') {
        return;
      }

      badge.dataset.naturkidzProcessed = 'true';

      if (badge.textContent.trim().toLowerCase() !== 'on sale') {
        return;
      }

      const badgeContainer = badge.parentElement;
      badge.remove();

      if (badgeContainer && !badgeContainer.children.length) {
        badgeContainer.remove();
      }
    });
  }

  async function enhanceGridPricing() {
    const cards = Array.from(document.querySelectorAll(FUNDLET_CARD_SELECTOR));

    for (const card of cards) {
      if (card.dataset.naturkidzPriceProcessed === 'true') {
        continue;
      }

      card.dataset.naturkidzPriceProcessed = 'true';

      const handle = card.dataset.handle;
      const priceContainer = card.querySelector(FUNDLET_PRICE_SELECTOR);

      if (!handle || !priceContainer || priceContainer.querySelector('.nk-fundlet-compare-price')) {
        continue;
      }

      try {
        const product = await getProduct(handle);
        const compareAtPrice = getCompareAtPrice(product);
        const activePrice = getCurrentPrice(product);

        if (!compareAtPrice || !activePrice || compareAtPrice <= activePrice) {
          continue;
        }

        priceContainer.classList.add('nk-fundlet-price-row');

        const compareAtNode = document.createElement('p');
        compareAtNode.className = 'nk-fundlet-compare-price';
        compareAtNode.textContent = formatMoney(compareAtPrice);

        const currentPriceNode = priceContainer.querySelector('p, span, div');

        if (currentPriceNode) {
          priceContainer.insertBefore(compareAtNode, currentPriceNode);
        } else {
          priceContainer.appendChild(compareAtNode);
        }
      } catch (error) {
        card.dataset.naturkidzPriceProcessed = 'error';
      }
    }
  }

  async function getProduct(handle) {
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    const request = fetch(`/products/${handle}.js`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load product data for ${handle}`);
        }

        return response.json();
      })
      .then((product) => {
        productCache.set(handle, product);
        return product;
      });

    productCache.set(handle, request);
    return request;
  }

  function getCompareAtPrice(product) {
    const variantCompareAtPrices = Array.isArray(product.variants)
      ? product.variants
        .map((variant) => variant.compare_at_price)
        .filter((price) => typeof price === 'number' && price > 0)
      : [];

    if (variantCompareAtPrices.length) {
      return Math.max(...variantCompareAtPrices);
    }

    return product.compare_at_price_max || product.compare_at_price || null;
  }

  function getCurrentPrice(product) {
    if (typeof product.price === 'number' && product.price > 0) {
      return product.price;
    }

    if (typeof product.price_min === 'number' && product.price_min > 0) {
      return product.price_min;
    }

    return null;
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(
        cents,
        window.theme?.moneyFormat || window.Shopify.money_format || '${{amount}}'
      );
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en-NZ', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'NZD',
    }).format(cents / 100);
  }
})();
